import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config.js';

export function useCambiarDatos() {
    const step = ref(1);
    const pedido = ref(null);
    const entradasSeleccionadas = ref([]);
    const datosNuevos = ref({});
    const datosCompra = ref(null);
    const loading = ref(false);
    const error = ref(null);

    const costePorCambio = computed(() =>
        parseFloat(pedido.value?.evento?.coste_cambio_nombre ?? 0)
    );

    const totalCambios = computed(() =>
        entradasSeleccionadas.value.length * costePorCambio.value
    );

    const terminoEntrada = computed(() => store.getters.getTeatro?.termino_entrada || '');
    const terminoEntradas = computed(() => store.getters.getTeatro?.termino_entradas || '');

    const isFormValid = computed(() => {
        return entradasSeleccionadas.value.every(e => {
            const d = datosNuevos.value[e.id];
            if (!d) return false;
            return (
                d.nombre.trim().length > 0 &&
                d.apellidos.trim().length > 0 &&
                d.tipo_documento.trim().length > 0 &&
                d.dni.trim().length > 0
            );
        });
    });

    const esDuplicado = computed(() => {
        const dnis = new Set();
        for (const e of entradasSeleccionadas.value) {
            const d = datosNuevos.value[e.id];
            if (!d || !d.dni) continue;
            const dniNorm = String(d.dni).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
            if (dnis.has(dniNorm)) return true;
            dnis.add(dniNorm);
        }
        return false;
    });

    const iniciarFlujo = pedidoObj => {
        pedido.value = pedidoObj;
        entradasSeleccionadas.value = [];
        datosNuevos.value = {};
        loading.value = false;
        error.value = null;
        step.value = 1;
    };

    const seleccionarEntradas = entradas => {
        entradasSeleccionadas.value = entradas;
        const mapa = {};
        entradas.forEach(e => {
            mapa[e.id] = { nombre: '', apellidos: '', tipo_documento: 'DNI', dni: '' };
        });
        datosNuevos.value = mapa;
    };

    const actualizarDato = (ticketId, campo, valor) => {
        if (!datosNuevos.value[ticketId]) return;
        // Reasignación completa para garantizar reactividad en Vue 2.7
        datosNuevos.value = {
            ...datosNuevos.value,
            [ticketId]: { ...datosNuevos.value[ticketId], [campo]: valor },
        };
    };

    const guardarDatos = async() => {
        if (loading.value) return;
        loading.value = true;
        error.value = null;

        try {
            const items = entradasSeleccionadas.value.map(e => ({
                user_ticket_id: e.id,
                nombre: datosNuevos.value[e.id].nombre,
                apellidos: datosNuevos.value[e.id].apellidos,
                dni: datosNuevos.value[e.id].dni,
            }));

            const res = await store.dispatch(
                config.getVuexAlias('front.perfil.pedidos.carrito.crear'),
                { filters: {}, items }
            );
            const { session_id, jet_id, formulario, total } = res.data;

            datosCompra.value = {
                session_id,
                total_resumen: total,
                ...(jet_id && { jet_id }),
                ...(formulario && { formulario }),
            };
            step.value = 3;
        } catch (err) {
            error.value = err.response?.data?.message || 'Error al guardar los datos';
        } finally {
            loading.value = false;
        }
    };

    const obtenerDatosActualizados = () => {
        return entradasSeleccionadas.value.map(entrada => ({
            id: entrada.id,
            user_nombre: datosNuevos.value[entrada.id]?.nombre || '',
            user_apellidos: datosNuevos.value[entrada.id]?.apellidos || '',
            user_dni: datosNuevos.value[entrada.id]?.dni || '',
        }));
    };

    const reset = () => {
        step.value = 1;
        pedido.value = null;
        entradasSeleccionadas.value = [];
        datosNuevos.value = {};
        datosCompra.value = null;
        loading.value = false;
        error.value = null;
    };

    return {
        step,
        pedido,
        entradasSeleccionadas,
        datosNuevos,
        datosCompra,
        loading,
        error,
        isFormValid,
        esDuplicado,
        costePorCambio,
        totalCambios,
        terminoEntrada,
        terminoEntradas,
        iniciarFlujo,
        seleccionarEntradas,
        actualizarDato,
        guardarDatos,
        obtenerDatosActualizados,
        reset,
    };
}
